import { useCallback } from 'react';
import { useHistory } from 'react-router';
import { useQuery, useSubscription } from '@apollo/client';

import { OutstandingAgreementType } from '__generated__/GQL';
import asBentoModule from 'common/bento/hoc/asBentoModule';
import withStateMachine from 'common/bento/hoc/withStateMachine/withStateMachine';
import assertNever from 'common/bento/lib/assertNever';
import stringifyState from 'common/bento/lib/stringifyState';
import Agreement from 'common/bento/modules/Agreement';
import { BentoModuleDoneStatus } from 'common/bento/types/BentoModule';
import useCompanyContext from 'common/hooks/useCompanyContext';
import SuspenseQuery from 'components/SuspenseQuery';
import NavigationContext from 'features/Onboarding/components/OBNavigation/NavigationContext';
import OnboardingPageAdapter from 'features/Onboarding/components/OnboardingPageAdapter';
import BeneficiariesDocs from 'features/Onboarding/legacy/Pages/BeneficiariesDocs';
import DownloadShineMessageSent from 'features/Onboarding/legacy/Pages/DownloadShineMessageSent';
import CompanyCompliance from 'features/Onboarding/modules/CompanyCompliance';
import Compliance from 'features/Onboarding/modules/Compliance/Compliance';
import StepsPricingPlan from 'features/Onboarding/modules/StepsPricingPlan';
import Verification from 'features/Onboarding/modules/Verification';
import CapitalDepositNextSteps from 'features/Onboarding/pages/CapitalDepositNextSteps';
import { ApplicantType, ApplicationType } from 'features/Onboarding/types';
import logger from 'helpers/logger';

import OBLoaderPage from '../../components/OBLoaderPage/OBLoaderPage';
import useOverrideSetCurrentPage from '../../hooks/useOverrideSetCurrentPage';
import { User } from '../../legacy/types';
import CapitalDepositEUBankAccount from '../../modules/CapitalDepositEUBankAccount';
import { SasuCreationDataDocument } from './graphql/queries/sasuCreationData.gql';
import { OnCompanyCreationSasuInfoSetDocument } from './graphql/subscriptions/onCompanyCreationSASUInfoSet.gql';
import machineConfig, { model, State } from './machine';
import getNavigationSteps from './navigation/getNavigationSteps';
import ApplicationFollowUp from './pages/ApplicationFollowUp';
import SasuCreationLandbot from './pages/SasuCreationLandbot';
import SasuCreationLandbotSuccess from './pages/SasuCreationLandbotSuccess';
import SasuCreationPending from './pages/SasuCreationPending';

const logImpossibleEvent = (event: string, state: string) => {
  logger.error(`Impossible event '${event}' in state ${state}`);
};

const OnboardingSasuCreationComponent = withStateMachine<typeof model>(
  ({ machine, moduleData }) => {
    const [state, send] = machine;

    const onPrev = useCallback(() => send(model.events.PREV()), [send]);

    const adaptedPageProps = {
      onPrev,
      setCurrentPage: useOverrideSetCurrentPage(send),
      user: moduleData.viewer.profile as unknown as User,
    };

    const stringifiedState = stringifyState<State>(state);
    const { company } = moduleData.viewer;
    const { companyProfileId } = company;

    useSubscription(OnCompanyCreationSasuInfoSetDocument, {
      onData: ({ data: subscriptionData }) => {
        const { error } = subscriptionData;
        if (error === undefined) {
          send('SASU_INFO_SET');
        }
      },
    });

    const handlePendingDone = useCallback(() => {
      send('NEXT');
    }, [send]);

    const getContent = () => {
      switch (stringifiedState) {
        case State.SASU_CREATION_LANDBOT: {
          if (!moduleData.viewer.profile.email) {
            throw new Error(`email required in state ${stringifiedState}`);
          }

          if (!moduleData.viewer.profile.phone) {
            throw new Error(`phone required in state ${stringifiedState}`);
          }

          return (
            <SasuCreationLandbot
              customData={{
                company_profile_id: companyProfileId,
                email: moduleData.viewer.profile.email,
                environment:
                  import.meta.env.VITE_API_ENV === 'production'
                    ? 'production'
                    : 'staging',
                first_name: moduleData.viewer.profile.firstName,
                last_name: moduleData.viewer.profile.lastName,
                nom_epouse: moduleData.viewer.profile.lastName,
                phone: moduleData.viewer.profile.phone,
                prenom_principal: moduleData.viewer.profile.firstName,
                uid: moduleData.viewer.uid,
              }}
              onDone={() => {
                send('NEXT');
              }}
              onPrev={() => send('PREV')}
              onRedirectToPartner={() => {
                send('REDIRECTED_TO_PARTNER');
              }}
            />
          );
        }

        case State.SASU_CREATION_LANDBOT_PENDING:
          return (
            <SasuCreationPending
              hasProcessedInfo={!!moduleData.viewer.company?.profile.legalName}
              onDone={handlePendingDone}
              onPrev={() => send('PREV')}
            />
          );

        case State.SASU_CREATION_LANDBOT_SUCCESS: {
          return (
            <SasuCreationLandbotSuccess
              onContinue={() => send('NEXT')}
              onPrev={() => send('PREV')}
            />
          );
        }

        case State.PERSONAL_COMPLIANCE:
          return (
            <Compliance
              companyProfileId={companyProfileId}
              onDone={() => send(model.events.NEXT())}
            />
          );

        case State.VERIFICATION: {
          return (
            <Verification
              applicantType={ApplicantType.AccountHolder}
              applicationType={ApplicationType.SasuCreation}
              companyProfileId={companyProfileId}
              isMainApplicant
              onDone={() => send('NEXT')}
            />
          );
        }

        case State.SASU_CREATION_AGREEMENT: {
          return (
            <Agreement
              onDone={({ status }) =>
                send(status === BentoModuleDoneStatus.DONE ? 'NEXT' : 'PREV')
              }
              type={OutstandingAgreementType.NotaryFourez}
            />
          );
        }

        case State.USER_AGREEMENT: {
          return (
            <Agreement
              onDone={({ status }) =>
                send(status === BentoModuleDoneStatus.DONE ? 'NEXT' : 'PREV')
              }
              type={OutstandingAgreementType.Cgu}
            />
          );
        }

        case State.SASU_CREATION_EU_BANK_ACCOUNT:
          return (
            <CapitalDepositEUBankAccount
              onDone={({ status }) =>
                send(status === BentoModuleDoneStatus.DONE ? 'NEXT' : 'PREV')
              }
            />
          );

        case State.INIT:
        case State.USER_PROFILE_SEND:
        case State.DONE:
        case State.ABORT:
        case State.SASU_CREATION_LANDBOT_ABORTING:
        case State.SASU_CREATION_CREATE:
        case State.SASU_CREATION_SUBMIT_APPLICATION:
          return <OBLoaderPage onPrev={() => null} />;
      }

      switch (stringifiedState) {
        case State.COMPANY_COMPLIANCE: {
          return (
            <CompanyCompliance
              companyProfileId={companyProfileId}
              isMandatee={false}
              onDone={({ status }) =>
                send(status === BentoModuleDoneStatus.DONE ? 'NEXT' : 'PREV')
              }
            />
          );
        }

        case State.SASU_CREATION_PLAN_CHOICE: {
          return (
            <StepsPricingPlan
              onDone={({ status }) =>
                status === BentoModuleDoneStatus.DONE
                  ? send('NEXT')
                  : send('PREV')
              }
            />
          );
        }

        case State.APPLICATION_FOLLOW_UP: {
          if (!company?.capitalDeposit) {
            throw new Error(
              `capitalDeposit required in state ${stringifiedState}`,
            );
          }
          if (company.profile.legalNumberOfShares === null) {
            throw new Error(
              `legalNumberOfShares required in state ${stringifiedState}`,
            );
          }
          if (company.capitalDeposit.paidUpCapitalPercentage === null) {
            throw new Error(
              `paidUpCapitalPercentage required in state ${stringifiedState}`,
            );
          }

          return (
            <ApplicationFollowUp
              applicationFollowUpData={{
                capitalDeposit: {
                  ...company.capitalDeposit,
                  paidUpCapitalPercentage:
                    company.capitalDeposit.paidUpCapitalPercentage,
                },
                companyProfile: {
                  ...company.profile,
                  legalNumberOfShares: company.profile.legalNumberOfShares,
                },
                creationSASU: company.creationSASU,
                kybDocuments: company.kybDocuments,
                legalBeneficiaries: company.legalBeneficiaries,
                userProfile: {
                  ...moduleData.viewer.profile,
                  email: moduleData.viewer.companyUser?.email ?? '',
                },
              }}
              isInitialSubmission={
                state.event.type === 'done.invoke.submitApplication'
              }
              onPrev={onPrev}
            />
          );
        }

        case State.SASU_CREATION_NEXT_STEPS: {
          return (
            <CapitalDepositNextSteps
              goToBeneficiaries={() => send('GO_BENEFICIARIES_DOCS')}
              goToSmsSent={() => send('DOWNLOAD_SHINE')}
              onPrev={onPrev}
            />
          );
        }

        case State.BENEFICIARIES_DOCS: {
          return (
            <OnboardingPageAdapter
              {...adaptedPageProps}
              LegacyOnboardingPage={BeneficiariesDocs}
              setCurrentPage={(page) => {
                switch (page) {
                  case 'verification-home':
                    return send('PREV');

                  default:
                    return logImpossibleEvent(page, stringifiedState);
                }
              }}
            />
          );
        }

        case State.DOWNLOAD_SHINE_MESSAGE_SENT: {
          return (
            <OnboardingPageAdapter
              LegacyOnboardingPage={DownloadShineMessageSent}
              {...adaptedPageProps}
              onPrev={onPrev}
            />
          );
        }

        default: {
          return assertNever(stringifiedState);
        }
      }
    };

    return (
      <NavigationContext.Provider
        value={{ steps: getNavigationSteps(stringifyState<State>(state)) }}
      >
        {getContent()}
      </NavigationContext.Provider>
    );
  },

  {
    devTools: true,
    machineConfig,
  },
);

const OnboardingSasuCreation = asBentoModule<{
  companyProfileId: string;
}>(({ companyProfileId, onDone }) => {
  const history = useHistory();
  const companyContext = useCompanyContext();

  const query = useQuery(SasuCreationDataDocument, {
    variables: {
      companyProfileId: companyProfileId,
    },
  });

  return (
    <SuspenseQuery loader={<OBLoaderPage onPrev={() => null} />} query={query}>
      {(data) => {
        if (!data.viewer.company) {
          throw new Error(
            'company is missing in SasuCreation, this should not happen',
          );
        }

        return (
          <OnboardingSasuCreationComponent
            moduleData={{
              ...data,
              companyContext,
              getQueryParams: () =>
                new URLSearchParams(history.location.search),
            }}
            onDone={onDone}
          />
        );
      }}
    </SuspenseQuery>
  );
});

export default OnboardingSasuCreation;
